import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { supabase } from '../supabaseClient';
import useAnalytics from '../analytics/analytics';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { isWorkEmail, getWorkEmailErrorMessage } from '../../utils/emailValidation';
import { useAimdoc } from '@aimdoc/sdk-react';
import { AimdocLogo } from '../brand/AimdocLogo';
import { resolveInviteLanding, type InviteLoadState, type InvitePreview } from '../../lib/invite';

const TRUSTED_LOGOS = [
  { name: 'QuotaPath', src: '/login/quotapath.svg' },
  { name: 'Evocalize', src: '/login/evocalize.webp' },
  { name: 'Youdera', src: '/login/youdera.svg' },
  { name: 'Blawgy', src: '/login/blawgy.png' },
  { name: 'PointGuard', src: '/login/pointguard.png' },
  // { name: 'Digideck', src: '/login/digideck.png' },
];

const TESTIMONIALS = [
  {
    quote: "We saw a 250% increase in organic leads within the first three months of using Aimdoc.",
    name: "Justin Ulrich",
    role: "VP of Marketing, Evocalize",
    img: "/login/justin_evocalize.jpeg",
  },
  {
    quote: "Aimdoc has become an instrumental part of our funnel. Even when it's not the final conversion point, it engages nearly every visitor and moves them closer to becoming a customer.",
    name: "Travis Northup",
    role: "Director of Demand Generation, QuotaPath",
    img: "https://framerusercontent.com/images/zUDH9TZBaB1QofwSgBV7o9UL4.jpg",
  },
  {
    quote: "This is a powerful platform that leverages generative AI more effectively than other platforms I've tested. On top of that, the team is terrific to work with.",
    name: "William Leichter",
    role: "CMO, PointGuard AI",
    img: "/login/willy_leichter.jpeg",
  },
];

function BrandedPanel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      const timeout = setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % TESTIMONIALS.length);
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timeout);
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  const testimonial = TESTIMONIALS[activeIndex];

  return (
    <div
      className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-blue-700 via-blue-500 to-sky-400 p-10 xl:p-14 overflow-hidden relative"
      style={{ width: '60vw', minWidth: '60vw', flexShrink: 0 }}
    >
      <div className="absolute inset-0">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute bottom-20 -left-16 w-72 h-72 rounded-full bg-white/[0.03]" />
        <div className="absolute top-1/2 right-1/4 w-48 h-48 rounded-full bg-white/[0.04]" />
      </div>

      <div className="relative z-10">
        <AimdocLogo mono className="h-9 w-auto text-white" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-2xl">
        <svg className="h-8 w-8 text-blue-200/60 mb-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" />
        </svg>
        <div
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          <blockquote className="text-xl xl:text-2xl font-semibold text-white leading-relaxed mb-8">
            &ldquo;{testimonial.quote}&rdquo;
          </blockquote>
          <div className="flex items-center gap-4">
            <img
              src={testimonial.img}
              alt={testimonial.name}
              className="h-14 w-14 rounded-full ring-2 ring-white/30 ring-offset-2 ring-offset-transparent object-cover"
            />
            <div>
              <div className="font-semibold text-white">{testimonial.name}</div>
              <div className="text-sm text-blue-100/80">{testimonial.role}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <p className="text-xs font-medium text-blue-200/60 uppercase tracking-wider mb-4">
          Trusted by marketing teams around the world
        </p>
        <div className="flex items-center gap-6 flex-wrap">
          {TRUSTED_LOGOS.map((logo) => (
            <img
              key={logo.name}
              src={logo.src}
              alt={logo.name}
              className="h-6 brightness-0 invert opacity-60"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function LoginLayout({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        zIndex: 50,
      }}
      className="bg-background"
    >
      <BrandedPanel />
      <div style={{ flex: '1 1 0%', minWidth: 0, overflowY: 'auto' }} className="flex items-center justify-center px-6 py-10 sm:px-10">
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <div className="lg:hidden mb-8 flex justify-center">
            <AimdocLogo className="h-9 w-auto text-foreground" />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

const OAUTH_FLOW_STORAGE_KEY = 'pendingOAuthFlowParams';

function Login() {
  const navigate = useNavigate();
  const { captureEvent } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const inviteId = searchParams.get('invite_id');
  // /register (the marketing site's "Start free" target) and ?mode=signup open
  // straight onto the signup form; plain /login stays a sign-in page.
  const signUpRequested = location.pathname === '/register' || searchParams.get('mode') === 'signup';
  // Invitees land here from an emailed link, so start on the signup form rather
  // than flashing the sign-in form while the invite lookup is in flight.
  const [isSignUp, setIsSignUp] = useState(() => Boolean(inviteId) || signUpRequested);
  const [invitePreview, setInvitePreview] = useState<InvitePreview | null>(null);
  const [inviteState, setInviteState] = useState<InviteLoadState>(() => (inviteId ? 'loading' : 'idle'));
  const [isConfirmationSent, setIsConfirmationSent] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [isPasswordRecoverySent, setIsPasswordRecoverySent] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const hasAttemptedOAuthResume = useRef(false);
  const { sendMessage } = useAimdoc();
  const onboardingMessageSentRef = useRef(false);

  const storeOAuthParamsFromUrl = useCallback(() => {
    const nextUrl = searchParams.get('next');
    const requestId = searchParams.get('request_id');

    if (nextUrl === '/oauth/authorize' && requestId) {
      localStorage.setItem(OAUTH_FLOW_STORAGE_KEY, JSON.stringify({ request_id: requestId }));
      return true;
    }
    return false;
  }, [searchParams]);

  const checkAndResumeOAuthFlow = useCallback(async () => {
    if (hasAttemptedOAuthResume.current) {
      return false;
    }
    const storedOAuthParamsString = localStorage.getItem(OAUTH_FLOW_STORAGE_KEY);
    if (storedOAuthParamsString) {
      try {
        const params = JSON.parse(storedOAuthParamsString);
        localStorage.removeItem(OAUTH_FLOW_STORAGE_KEY);

        if (!params.request_id) {
          return false;
        }
        const query = new URLSearchParams();
        query.set('request_id', params.request_id);

        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          query.set('frontend_authenticated', 'true');
        }

        const resumeUrlPath = `/oauth/authorize?${query.toString()}`;
        const apiBase = import.meta.env.VITE_API_URL;
        const fullResumeUrl = `${apiBase}${resumeUrlPath}`;

        hasAttemptedOAuthResume.current = true;
        window.location.href = fullResumeUrl;
        return true;
      } catch (e) {
        console.error("Login.tsx: Error parsing stored OAuth params", e);
        localStorage.removeItem(OAUTH_FLOW_STORAGE_KEY);
      }
    }
    return false;
  }, []);

  // Effect for handling error messages from URL params
  useEffect(() => {
    const errorParam = searchParams.get('error');
    
    if (errorParam === 'work_email_required') {
      toast.error('Work email required. Please sign up with your company email address.');
      // Remove error param from URL without reloading
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error');
      const newUrl = newSearchParams.toString() ? `?${newSearchParams.toString()}` : location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams, location.pathname]);

  // Touchpoint 1: Detect onboarding visitors and send system message
  // TEMPORARILY DISABLED: When dogfooding Aimdoc on Aimdoc itself, the agent gets
  // confused between configuring its own form/widget and helping the user configure
  // their Aimdoc account. This caused it to start creating fields based on its own
  // sign-up form rather than the user's website. Re-enable once we have a clean
  // separation between the dogfooded agent's context and the user's onboarding flow.
  // useEffect(() => {
  //   const visitorId = searchParams.get('aimdoc_visitor_id');
  //   const startTrial = searchParams.get('aimdoc_start_trial');
  //
  //   if (visitorId || startTrial) {
  //     localStorage.setItem('aimdoc_onboarding_source', visitorId ? 'visitor' : 'trial');
  //   }
  //
  //   if (!onboardingMessageSentRef.current && (visitorId || startTrial)) {
  //     onboardingMessageSentRef.current = true;
  //     const message = visitorId
  //       ? 'The user just arrived from the website to start a trial. They were chatting with the website agent before being redirected here to sign up.'
  //       : 'A new user has arrived to start a trial. Guide them through signing up.';
  //     const timerId = setTimeout(() => {
  //       sendMessage(message, { agentId: import.meta.env.VITE_PUBLIC_AGENT_ID });
  //     }, 2000);
  //     return () => clearTimeout(timerId);
  //   }
  // }, [searchParams, sendMessage]);

  // Effect for loading the invite behind ?invite_id= so the page can render an
  // invite-aware signup state (org + inviter) instead of a generic sign-in form.
  useEffect(() => {
    if (!inviteId) {
      setInvitePreview(null);
      setInviteState('idle');
      // Signup mode here is derived from the invite id, so it has to come back
      // down with it. Without this, navigating from /login?invite_id= to plain
      // /login keeps the trial signup form instead of the default sign-in view.
      // /register asked for signup explicitly, so it keeps the form.
      setIsSignUp(signUpRequested);
      return;
    }

    let cancelled = false;
    setInviteState('loading');

    const loadInvite = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/invites/${encodeURIComponent(inviteId)}/preview`
        );
        if (cancelled) return;

        if (response.status === 404) {
          setInvitePreview(null);
          setInviteState('not_found');
          setIsSignUp(signUpRequested);
          return;
        }
        if (!response.ok) {
          throw new Error(`Invite lookup failed with status ${response.status}`);
        }

        const preview: InvitePreview = await response.json();
        if (cancelled) return;

        setInvitePreview(preview);
        setInviteState('loaded');
        // An already-accepted invite means the account exists — send them to sign in.
        setIsSignUp(preview.status !== 'accepted');
        if (preview.email) {
          setEmail((current) => current || preview.email);
        }
      } catch (error) {
        if (cancelled) return;
        // Keep the signup state: we know an invite id was present, we just
        // couldn't resolve the org name to personalize the page.
        console.error('Login.tsx: Error loading invite preview', error);
        setInvitePreview(null);
        setInviteState('error');
      }
    };

    loadInvite();

    return () => {
      cancelled = true;
    };
  }, [inviteId, signUpRequested]);

  // Effect for handling Recovery Mode based ONLY on searchParams
  useEffect(() => {
    const recoveryParam = searchParams.get('recovery');

    if (recoveryParam === 'true') {
      const hash = window.location.hash;
      if (hash.includes('access_token') && hash.includes('type=recovery')) {
        if (!isRecoveryMode) {
          setIsRecoveryMode(true);
          setIsSignUp(false);
          setIsConfirmationSent(false);
          setIsPasswordRecoverySent(false);
        }
      } else {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session && !isRecoveryMode) {
            setIsRecoveryMode(true);
          }
        });
      }
    } else {
      if (isRecoveryMode) {
        setIsRecoveryMode(false);
      }
    }
  }, [searchParams, isRecoveryMode]);

  useEffect(() => {
    storeOAuthParamsFromUrl();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        if (hasAttemptedOAuthResume.current) {
          return; 
        }
        
        checkAndResumeOAuthFlow().then(didAttemptResume => {
          if (didAttemptResume) {
            return;
          }

          const isOnAuthPage = location.pathname === '/login' || location.pathname === '/register';
          const hasInvite = !!searchParams.get('invite_id');
          const inRecoveryFromUrl = !!searchParams.get('recovery');

          if (isOnAuthPage && !hasInvite && !inRecoveryFromUrl) {
            // Touchpoint 2: Send system message after sign-in for onboarding users
            // TEMPORARILY DISABLED: see note on Touchpoint 1 above. The dogfooded agent
            // confuses its own sign-up form context with the user's configuration task.
            // if (localStorage.getItem('aimdoc_onboarding_source')) {
            //   sendMessage(
            //     'The user has successfully signed in. They will be guided to create their AI agent next.',
            //     { agentId: import.meta.env.VITE_PUBLIC_AGENT_ID }
            //   );
            // }
            navigate('/');
          } else {
          }
        }).catch(error => {
          console.error("Login.tsx: Error during checkAndResumeOAuthFlow execution:", error);
        });

      } else if (event === 'SIGNED_OUT') {
        hasAttemptedOAuthResume.current = false;
      }
    });

    if (inviteId) {
      localStorage.setItem('pendingInviteId', inviteId);
    }

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [storeOAuthParamsFromUrl, checkAndResumeOAuthFlow, navigate, location.pathname, searchParams, inviteId]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    hasAttemptedOAuthResume.current = false;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      captureEvent('logged_in', {});
    } catch (error: any) {
      toast.error(error.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    hasAttemptedOAuthResume.current = false;
    const normalizedFirstName = firstName.trim();
    const normalizedLastName = lastName.trim();

    if (!normalizedFirstName || !normalizedLastName) {
      toast.error('First name and last name are required');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Validate work email domain
    if (!isWorkEmail(email)) {
      toast.error(getWorkEmailErrorMessage());
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: normalizedFirstName,
            last_name: normalizedLastName,
            full_name: `${normalizedFirstName} ${normalizedLastName}`.trim(),
          },
        },
      });
      if (error) throw error;
      if (data.user?.identities?.length === 0) {
        toast.error('An account with this email already exists');
      } else {
        setSignupEmail(email);
        setIsConfirmationSent(true);
        captureEvent('signed_up', {});
      }
    } catch (error: any) {
      toast.error(error.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    hasAttemptedOAuthResume.current = false;
    storeOAuthParamsFromUrl();

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/oauth-callback'
        }
      });
      if (error) {
        toast.error(error.message || 'An error occurred during Google login');
        setLoading(false);
        throw error;
      }
    } catch (error: any) {
      console.error("Google login error:", error);
      if (loading && !(error.message && error.message.toLowerCase().includes('redirect'))) {
        setLoading(false);
      }
    }
  };

  const handlePasswordRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/login?recovery=true',
      });
      if (error) throw error;
      setRecoveryEmail(email);
      setIsPasswordRecoverySent(true);
      captureEvent('password_reset_requested', {});
      toast.success('Password recovery email sent. Check your inbox.');
    } catch (error: any) {
      toast.error(error.message || 'An error occurred while sending recovery email.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long.');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
      toast.success('Password updated successfully! Please sign in with your new password.');
      
      // Clear recovery mode and form fields
      setIsRecoveryMode(false); // Set internal state first
      setNewPassword('');
      setConfirmNewPassword('');
      setEmail(data.user?.email || ''); 
      setPassword('');

      // Navigate to /login to clear URL parameters and reset view
      navigate('/login', { replace: true }); 

    } catch (error: any) {
      toast.error(error.message || 'An error occurred while updating your password.');
    } finally {
      setLoading(false);
    }
  };

  if (isConfirmationSent) {
    return (
      <LoginLayout>
        <div className="text-center space-y-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Check your email</h2>
            <p className="text-sm text-muted-foreground mt-2">
              We sent a confirmation link to <span className="font-medium">{signupEmail}</span>
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            Click the link in the email to confirm your account. If you don't see the email, check your spam folder.
          </p>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              setIsConfirmationSent(false);
              setIsSignUp(false);
              setFirstName('');
              setLastName('');
              setEmail('');
              setPassword('');
              setConfirmPassword('');
            }}
          >
            Back to Sign In
          </Button>
        </div>
      </LoginLayout>
    );
  }

  if (isPasswordRecoverySent) {
    return (
      <LoginLayout>
        <div className="text-center space-y-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Check your email</h2>
            <p className="text-sm text-muted-foreground mt-2">
              We sent a password recovery link to <span className="font-medium">{recoveryEmail}</span>
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            Click the link in the email to reset your password. If you don't see the email, check your spam folder.
          </p>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              setIsPasswordRecoverySent(false);
              setEmail('');
            }}
          >
            Back to Sign In
          </Button>
        </div>
      </LoginLayout>
    );
  }

  if (isRecoveryMode) {
    return (
      <LoginLayout>
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight">Set New Password</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Please enter your new password below.
            </p>
          </div>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirm New Password</Label>
              <Input
                id="confirm-new-password"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Updating Password...' : 'Update Password'}
            </Button>
          </form>
          <div className="text-center">
            <button
              onClick={() => {
                setIsRecoveryMode(false);
                navigate('/login', { replace: true });
              }}
              className="text-sm text-primary hover:underline font-medium"
              type="button"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </LoginLayout>
    );
  }

  const inviteLanding = resolveInviteLanding(invitePreview, inviteState);
  const showInviteCopy = inviteState !== 'idle' && isSignUp === inviteLanding.isSignUp;

  return (
    <LoginLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {showInviteCopy
              ? inviteLanding.heading
              : (isSignUp ? 'Sign up for a 14-day free trial' : 'Welcome back')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {showInviteCopy
              ? inviteLanding.subheading
              : (isSignUp
                ? 'Start improving your customer journey today'
                : 'Sign in to your account')}
          </p>
        </div>

        {inviteLanding.notice && (
          <div
            className={
              inviteLanding.noticeTone === 'warning'
                ? 'rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'
                : 'rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'
            }
          >
            {inviteLanding.notice}
          </div>
        )}

        <form onSubmit={isSignUp ? handleSignUp : handleLogin} className="space-y-4">
          {isSignUp && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first-name">First name</Label>
                <Input
                  id="first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">Last name</Label>
                <Input
                  id="last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{isSignUp ? 'Work email' : 'Email'}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={isSignUp ? 'Enter your work email' : 'you@example.com'}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {isSignUp && (
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          )}

          {!isSignUp && (
            <div className="text-xs text-muted-foreground text-right">
              <button
                type="button"
                onClick={handlePasswordRecovery}
                disabled={loading}
                className="text-primary hover:underline font-medium"
              >
                Forgot your password?
              </button>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading
              ? (isSignUp ? 'Creating Account...' : 'Signing in...')
              : (isSignUp ? 'Get Started' : 'Sign In')}
          </Button>
        </form>

        <p className="text-xs text-muted-foreground text-center">
          By proceeding, you agree to the{' '}
          <a
            href="https://www.iubenda.com/terms-and-conditions/36587222"
            className="underline hover:text-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>{' '}
          &{' '}
          <a
            href="https://www.iubenda.com/privacy-policy/36587222"
            className="underline hover:text-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </p>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or</span>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={handleGoogleLogin}
          className="w-full"
        >
          <img src="https://www.google.com/favicon.ico" alt="Google" className="h-4 w-4 mr-2" />
          {isSignUp ? 'Signup with Google' : 'Sign in with Google'}
        </Button>

        <p className="text-sm text-center text-muted-foreground">
          {isSignUp ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(false);
                  setFirstName('');
                  setLastName('');
                  setConfirmPassword('');
                }}
                className="text-primary hover:underline font-medium"
              >
                Log in
              </button>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => setIsSignUp(true)}
                className="text-primary hover:underline font-medium"
              >
                Sign up
              </button>
            </>
          )}
        </p>
      </div>
    </LoginLayout>
  );
}

export default Login;
